import OrgSwitcher from '@/components/org-switcher';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AccountReceivablePage } from '@/features/accountreceivable/AccountReceivablePage.js';
import { ManageAccountReceivables } from '@/features/accountreceivable/ManageAccountReceivables.js';
import { ChatbotPage } from '@/features/chatbot/ChatbotPage.js';
import { ManageComplaints } from '@/features/complaints/ManageComplaints.js';
import { ExperimentPage } from '@/features/experiment/ExperimentPage.js';
import { ManageExperiments } from '@/features/experiment/ManageExperiments.js';
import { LoginPage } from '@/features/home/LoginPage';
import { IndicatorScorePage } from '@/features/indicatorscore/IndicatorScorePage.js';
import { ManageIndicatorScores } from '@/features/indicatorscore/ManageIndicatorScores.js';
import { ManageKnowledgeBases } from '@/features/knowledgebase/ManageKnowledgeBases';
import { ManageOrganizations } from '@/features/organization/ManageOrganizations';
import { OrganizationPage } from '@/features/organization/OrganizationPage';
import { ManagePortfolios } from '@/features/portfolio/ManagePortfolios';
import { PortfolioCollaborators } from '@/features/portfolio/PortfolioCollaborators.js';
import { PortfolioPage } from '@/features/portfolio/PortfolioPage.js';
import { ManageProjects } from '@/features/project/ManageProjects';
import { ManageScoreLogics } from '@/features/scorelogic/ManageScoreLogics';
import { ScoreLogicPage } from '@/features/scorelogic/ScoreLogicPage.js';
import { Settings } from '@/features/settings/Settings.js';
import { SmartAnalyzePage } from '@/features/smartAnalyze/SmartAnalyzePage.js';
import { ManageTasks } from '@/features/tasks/ManageTasks.js';
import { ManageTranscriptions } from '@/features/transcription/ManageTranscription';
import UnifiedInboxBasePage from '@/features/UnifiedInbox/UnifiedinboxBasePage.js';
import { ExecutionDetails } from '@/features/workflow/components/ExecutionDetails.js';
import EmailNotificationOauthPopupHandler from '@/features/workflow/components/NodeConfigurations/EmailNotificationOAuthPopupHandler.js';
import EmailNotificationOutlookOauthPopupHandler from '@/features/workflow/components/NodeConfigurations/EmailNotificationOutlookOAuthPopupHandler.js';
import { ManageOrgWorkflows } from '@/features/workflow/ManageOrgWorkflows.js';
import { ManageWorkflows } from '@/features/workflow/ManageWorkflows.js';
import { WorkflowBuilder } from '@/features/workflow/WorkflowBuilder.js';
import { ManageWorkspaces } from '@/features/workspace/ManageWorkspaces';
import { WorkspaceCollaborators } from '@/features/workspace/WorkspaceCollaborators.js';
import { AircallOAuthRedirectHandler } from '@/integrations/aircall/oauth-redirect-handler.js';
import { QuickbooksOAuthRedirectHandler } from '@/integrations/quickbooks/oauth-redirect-handler.js';
import { ZendeskOAuthRedirectHandler } from '@/integrations/zendesk/oauth-redirect-handler.js';
import { StripeOAuthRedirectHandler } from '@/integrations/stripe/oauth-redirect-handler.js';
import { WhatsAppOAuthRedirectHandler } from '@/integrations/whatsApp/oauth-redirect-handler.js';
import { Bars3Icon, BellIcon } from '@heroicons/react/24/outline';
import { useStore } from '@nanostores/react';
import { useState, useEffect } from 'react';
import { HomePage } from '../features/home/HomePage';
import { ProjectPage } from '../features/project/ProjectPage';
import { WorkspacePage } from '../features/workspace/WorkspacePage';
import { $router } from '../stores/router.js';
import LeftSidebar from './LeftSidebar';

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarCollapsed = useStore($sideBarCollapsed); // Add global state for sidebar collapsed
  const [settingsActiveNav, setSettingsActiveNav] = useState('profile');
  const user = useStore($user);
  const page = useStore($router);
  const profile = useStore($profile);

  useEffect(() => {
    if (!profile && page?.route !== 'login') {
      $router.open(
        `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`,
      );
    }
  }, [profile, page?.route]);

  let contents: JSX.Element = <HomePage />;

  // Resolve the page to show.
  if (page?.route === 'home') {
    contents = <HomePage />;
  } else if (page?.route === 'login') {
    contents = <LoginPage />;
  } else if (page?.route === 'organizations') {
    contents = <ManageOrganizations />;
  } else if (page?.route === 'accountreceivables') {
    contents = <ManageAccountReceivables />;
  } else if (page?.route === 'accountreceivable') {
    contents = (
      <AccountReceivablePage
        accountReceivableId={page.params.accountreceivableId}
        portfolioId={page.params.portfolioId}
      />
    );
  } else if (page?.route === 'organization') {
    contents = <OrganizationPage organizationId={page.params.organizationId} />;
  } else if (page?.route === 'workspaceCollaborators') {
    contents = (
      <WorkspaceCollaborators
        workspaceId={page.params.workspaceId}
        organizationId={page.params.organizationId}
      />
    );
  } else if (page?.route === 'workspaces') {
    contents = <ManageWorkspaces organizationId={page.params.organizationId} />;
  } else if (page?.route === 'workspace') {
    contents = <WorkspacePage workspaceId={page.params.workspaceId} />;
  } else if (page?.route === 'portfolioCollaborators') {
    contents = (
      <PortfolioCollaborators
        portfolioId={page.params.portfolioId}
        organizationId={page.params.organizationId}
      />
    );
  } else if (page?.route === 'portfolios') {
    contents = <ManagePortfolios organizationId={page.params.organizationId} />;
  } else if (page?.route === 'portfolio') {
    contents = <PortfolioPage portfolioId={page.params.portfolioId} />;
  } else if (page?.route === 'projects') {
    contents = <ManageProjects />;
  } else if (page?.route === 'project') {
    contents = (
      <ProjectPage
        workspaceId={page.params.workspaceId}
        projectId={page.params.projectId}
      />
    );
  } else if (page?.route === 'workflows') {
    contents = <ManageWorkflows />;
  } else if (page?.route === 'workflows_o') {
    contents = <ManageOrgWorkflows />;
  } else if (page?.route === 'gmail_oauth_handler') {
    contents = <EmailNotificationOauthPopupHandler />;
  } else if (page?.route === 'outlook_oauth_handler') {
    contents = <EmailNotificationOutlookOauthPopupHandler />;
  } else if (page?.route === 'workflow') {
    contents = <WorkflowBuilder workflowId={page.params.workflowId} />;
  } else if (page?.route === 'workflow_o') {
    contents = <WorkflowBuilder workflowId={page.params.workflowId} />;
  } else if (page?.route === 'smartanalyze') {
    contents = <SmartAnalyzePage />;
  } else if (page?.route === 'chatbot') {
    contents = <ChatbotPage />;
  } else if (page?.route === 'knowledgebases') {
    contents = (
      <ManageKnowledgeBases organizationId={page.params.organizationId} />
    );
  } else if (page?.route === 'transcriptions') {
    contents = (
      <ManageTranscriptions organizationId={page.params.organizationId} />
    );
  } else if (page?.route === 'settings') {
    contents = <Settings activeNavId={settingsActiveNav} />;
  } else if (page?.route === 'zendesk_oauth_redirect') {
    contents = <ZendeskOAuthRedirectHandler />;
  } else if (page?.route === 'aircall_oauth_redirect') {
    contents = <AircallOAuthRedirectHandler />;
  } else if (page?.route === 'quickbooks_oauth_redirect') {
    contents = <QuickbooksOAuthRedirectHandler />;
  } else if (page?.route === 'stripe_oauth_redirect') {
    contents = <StripeOAuthRedirectHandler />;
  } else if (page?.route === 'whatsapp_oauth_redirect') {
    contents = <WhatsAppOAuthRedirectHandler />;
  } else if (page?.route === 'indicatorscores') {
    contents = (
      <ManageIndicatorScores organizationId={page.params.organizationId} />
    );
  } else if (page?.route === 'indicatorscore') {
    contents = (
      <IndicatorScorePage
        organizationId={page.params.organizationId}
        scoreIndicatorId={page.params.indicatorscoreId}
      />
    );
  } else if (page?.route === 'score_logics') {
    contents = (
      <ManageScoreLogics organizationId={page.params.organizationId} />
    );
  } else if (page?.route === 'score_logic') {
    contents = (
      <ScoreLogicPage
        scorelogicId={page.params.scorelogicId}
        organizationId={page.params.organizationId}
      />
    );
  } else if (page?.route === 'complaints') {
    contents = <ManageComplaints organizationId={page.params.organizationId} />;
  } else if (page?.route === 'experiments') {
    contents = (
      <ManageExperiments organizationId={page.params.organizationId} />
    );
  } else if (page?.route === 'experiment') {
    contents = (
      <ExperimentPage
        experimentId={page.params.experimentId}
        organizationId={page.params.organizationId}
      />
    );
  } else if (page?.route === 'workflow_execution_details') {
    contents = <ExecutionDetails workflowId={page.params.workflowId} />;
  } else if (page?.route === 'inbox') {
    contents = (
      <UnifiedInboxBasePage organizationId={page.params.organizationId} />
    );
  } else if (page?.route === 'tasks') {
    contents = <ManageTasks organizationId={page.params.organizationId} />;
  }

  const shell = (
    <div className="flex h-screen">
      {/* Left sidebar */}
      <LeftSidebar
        open={sidebarOpen}
        setOpen={setSidebarOpen}
        // Pass the collapsed state
        // Pass the setter for collapsed state
      />

      {/* Rest */}
      <div
        className={`lg:pl-${sidebarCollapsed ? '30' : '72'} flex flex-1 flex-col`}
      >
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b bg-background px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-muted-foreground lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Separator */}
          <div
            className="h-6 w-px bg-muted-foreground lg:hidden"
            aria-hidden="true"
          />

          {sidebarCollapsed && (
            <div className="ml-20 w-[260px]">
              <OrgSwitcher />
            </div>
          )}

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="relative flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-4">
              <Button variant="outline" size="icon">
                <BellIcon className="h-[1.2rem] w-[1.2rem]" />
                <span className="sr-only">View notifications</span>
              </Button>

              {/* <ModeToggle /> */}

              {/* Separator */}
              <div
                className="hidden lg:block lg:h-6 lg:w-px lg:bg-border"
                aria-hidden="true"
              />

              {/*-- Profile dropdown --*/}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="relative h-8 w-8 rounded-full"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={user?.photoURL || ''}
                        alt="Profile menu"
                      />
                      <AvatarFallback>
                        {user?.displayName?.slice(0, 2).toLocaleUpperCase() ||
                          'A'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="mr-2 w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.displayName || 'Anonymous'}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email || 'Not signed in'}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        setSettingsActiveNav('profile');
                        openPage($router, 'settings');
                      }}
                    >
                      Profile
                      <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setSettingsActiveNav('billing');
                        openPage($router, 'settings');
                      }}
                    >
                      Billing
                      <DropdownMenuShortcut>⌘B</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setSettingsActiveNav('profile');
                        openPage($router, 'settings');
                      }}
                    >
                      Settings
                      <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      firebaseConnector.logout();
                    }}
                  >
                    Log out
                    <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto py-4">
          <div
            className={`px-4 sm:px-6 ${sidebarCollapsed ? 'lg:pl-28 lg:pr-8' : 'lg:pl-[320px]'}`}
          >
            {contents}
          </div>
        </main>
      </div>
    </div>
  );

  const login = <LoginPage />;

  // Our page and layout.
  return page?.route === 'login' ? login : shell;
}
